/**
 * ePSF temperature helpers — IMP and storage ranges may each be °C or °F.
 * Always convert both sides to Celsius before comparing coverage.
 * Round to 4 dp so boundary pairs like 35.6°F–46.4°F vs 2°C–8°C compare cleanly.
 */

export function normalizeTempUnit(unit) {
  return String(unit || "C").trim().toUpperCase() === "F" ? "F" : "C";
}

export function toCelsius(value, unit) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const raw = normalizeTempUnit(unit) === "F" ? ((n - 32) * 5) / 9 : n;
  return Math.round(raw * 10000) / 10000;
}

export function formatTempRange(minValue, maxValue, unit, fallback = "") {
  const minOk =
    minValue !== "" && minValue != null && Number.isFinite(Number(minValue));
  const maxOk =
    maxValue !== "" && maxValue != null && Number.isFinite(Number(maxValue));
  if (!minOk || !maxOk) return String(fallback || "").trim();
  const min = Number(minValue);
  const max = Number(maxValue);
  const fmt = (n) =>
    Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  const u = normalizeTempUnit(unit) === "F" ? "\u00B0F" : "\u00B0C";
  return `${fmt(min)} - ${fmt(max)} ${u}`;
}

/**
 * Storage covers IMP when StorageMin <= ImpMin AND StorageMax >= ImpMax
 * on the same °C scale after converting each side with toCelsius.
 */
export function isStorageCompatibleWithImp({
  impMin,
  impMax,
  impUnit,
  storageMin,
  storageMax,
  storageUnit,
  storageMinC,
  storageMaxC,
}) {
  const impMinC = toCelsius(impMin, impUnit);
  const impMaxC = toCelsius(impMax, impUnit);
  const storMinC = toCelsius(
    storageMin != null ? storageMin : storageMinC,
    storageUnit != null ? storageUnit : "C",
  );
  const storMaxC = toCelsius(
    storageMax != null ? storageMax : storageMaxC,
    storageUnit != null ? storageUnit : "C",
  );
  if (
    impMinC == null ||
    impMaxC == null ||
    storMinC == null ||
    storMaxC == null
  ) {
    return null;
  }
  return storMinC <= impMinC && storMaxC >= impMaxC;
}

export function receiptTempStatus(tempValue, impMin, impMax) {
  const t = Number(tempValue);
  const min = Number(impMin);
  const max = Number(impMax);
  if (!Number.isFinite(t)) return null;
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (t < min || t > max) return "outside";
  return "within";
}

/**
 * Badge tone for an IMP storage temperature range (classified on °C).
 * frozen ≤0 · cold ≤8 (fridge) · cool ≤15 · ambient ≤25 · warm >25
 */
export function getStorageTempBadgeTone(minValue, maxValue, unit) {
  const minC = toCelsius(minValue, unit);
  const maxC = toCelsius(maxValue, unit);
  if (minC == null || maxC == null) return "default";
  const mid = (minC + maxC) / 2;
  if (maxC <= 0 || mid <= 0) return "frozen";
  if (maxC <= 8 || mid <= 8) return "cold";
  if (maxC <= 15 || mid <= 15) return "cool";
  if (maxC <= 25 || mid <= 25) return "ambient";
  return "warm";
}
